/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, Modal, IconButton, CircularProgress, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import PlaceIcon from '@mui/icons-material/Place';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ExploreIcon from '@mui/icons-material/Explore';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface PremisesItem {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
}

interface PremisesContent {
  title: string;
  description: string;
  premises: PremisesItem[];
}

export default function GalleryPremises() {
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<PremisesContent>({
    title: 'Temple Premises',
    description: 'Explore temple grounds',
    premises: [
      { id: '1', name: 'Main Sanctum', description: 'The sacred main sanctum of the temple.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_1.jpeg` },
      { id: '2', name: 'Mandapam Hall', description: 'The grand mandapam used for ceremonies.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_2.jpeg` },
      { id: '3', name: 'Outer Compound', description: 'The beautiful outer compound area.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_3.jpeg` },
      { id: '4', name: 'Temple Pond', description: 'The serene temple pond for rituals.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_4.jpeg` },
      { id: '5', name: 'Garden Grounds', description: 'The lush garden grounds of the temple.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_5.jpeg` },
      { id: '6', name: 'Annadanam Hall', description: 'The hall used for free food distribution.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_6.jpeg` },
      { id: '7', name: 'Temple Entrance', description: 'The majestic entrance of the temple.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_7.jpeg` },
      { id: '8', name: 'Sacred Spaces', description: 'Various sacred spots within the compound.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_8.jpeg` },
    ]
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'gallery_content', 'premises'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as PremisesContent);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading premises:', error);
      setLoading(false);
    }
  }, []);

  const premises = content.premises;

  const handleImageClick = (index: number) => { setCurrentIndex(index); setPreviewOpen(true); };
  const handleClosePreview = () => setPreviewOpen(false);
  const handlePrevImage = () => setCurrentIndex(currentIndex > 0 ? currentIndex - 1 : premises.length - 1);
  const handleNextImage = () => setCurrentIndex(currentIndex < premises.length - 1 ? currentIndex + 1 : 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Temple Premises — Kottayil Temple | ക്ഷേത്ര പരിസരം" description="Explore the premises of Kottayil Temple (Kotta-Kovilakam Kshethram) — Kottayil Kulam, temple grounds, sacred pond, and surroundings at Muchukunnu." path="/gallery/premises" keywords="Kottayil temple premises, temple grounds, Kottayil kulam, temple pond, Muchukunnu temple surroundings, കൊട്ടയിൽ കുളം, ക്ഷേത്ര പരിസരം" />
      <PageBanner title="Temple Premises" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{ borderRadius: '24px', overflow: 'hidden', mb: 3.5, boxShadow: '0 20px 60px rgba(20,184,166,0.15)' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #134e4a 0%, #14b8a6 55%, #99f6e4 100%)',
                px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 5, sm: 6 },
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  }}>
                    <TempleHinduIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontSize: { xs: '1.8rem', sm: '2.4rem' }, mb: 1.5, lineHeight: 1.1 }}>
                    Temple Premises
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.7, maxWidth: 500, mb: 2.5 }}>
                    Take a visual tour of our temple grounds, exploring the sanctum, halls, gardens, and sacred spaces.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<TempleHinduIcon sx={{ fontSize: 16, color: '#14b8a6 !important' }} />} label="Premises" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#0f766e', fontWeight: 700, fontSize: '0.7rem', height: 28, borderRadius: '14px' }} />
                    <Chip icon={<ExploreIcon sx={{ fontSize: 16, color: '#fff !important' }} />} label={`${premises.length} Locations`} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 28, borderRadius: '14px', backdropFilter: 'blur(10px)' }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* PREMISES GRID */}
            <Grid container spacing={2.5}>
              {premises.map((item, index) => {
                const pGradients: [string, string][] = [
                  ['#14b8a6', '#0d9488'], ['#8b5cf6', '#7c3aed'],
                  ['#f59e0b', '#d97706'], ['#ec4899', '#db2777'],
                  ['#3b82f6', '#2563eb'], ['#10b981', '#059669'],
                  ['#ef4444', '#dc2626'], ['#06b6d4', '#0891b2'],
                ];
                const gr = pGradients[index % pGradients.length];
                return (
                  <Grid item xs={6} sm={4} key={index}>
                    <Box
                      onClick={() => handleImageClick(index)}
                      sx={{
                        borderRadius: '20px', overflow: 'hidden', cursor: 'pointer',
                        position: 'relative',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                        transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
                          '& .premImg': { transform: 'scale(1.08)' },
                          '& .premOverlay': { opacity: 1 },
                        },
                      }}
                    >
                      <Box component="img" src={item.imageUrl} alt={item.name}
                        className="premImg"
                        sx={{ width: '100%', height: { xs: 160, sm: 220 }, objectFit: 'cover', display: 'block', transition: 'transform 600ms ease' }}
                      />
                      {/* Gradient name badge */}
                      <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                        px: 1.5, pb: 1.2, pt: 3.5,
                      }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: '8px', background: `linear-gradient(135deg, ${gr[0]}, ${gr[1]})` }}>
                          <PlaceIcon sx={{ fontSize: 11, color: '#fff' }} />
                          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.65rem' }}>
                            {item.name}
                          </Typography>
                        </Box>
                      </Box>
                      {/* Hover description overlay */}
                      <Box className="premOverlay" sx={{
                        position: 'absolute', inset: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.4) 70%)',
                        opacity: 0, transition: 'opacity 300ms ease',
                        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: 1.8,
                      }}>
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.78rem', mb: 0.3 }}>
                          {item.name}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', lineHeight: 1.5 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* FOOTER */}
            <Box sx={{
              mt: 3.5, borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              boxShadow: '0 8px 32px rgba(15,23,42,0.3)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3, position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Diversity3Icon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>Virtual Tour Coming Soon</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, maxWidth: 500, mx: 'auto' }}>
                  We are working on an interactive virtual tour of the temple premises. Stay tuned!
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="gallery" activePath="/gallery/premises" />
          </Grid>
        </Grid>
      </Container>

      {/* Image Preview Modal */}
      <Modal open={previewOpen} onClose={handleClosePreview} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.92)' }}>
        <Box sx={{ position: 'relative', outline: 'none' }}>
          <IconButton onClick={handleClosePreview} sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(20,184,166,0.9)', color: '#fff', '&:hover': { bgcolor: '#14b8a6' }, zIndex: 1 }}>
            <CloseIcon />
          </IconButton>
          {premises.length > 1 && (
            <IconButton onClick={handlePrevImage} sx={{ position: 'absolute', left: { xs: 8, md: 16 }, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(20,184,166,0.8)', color: '#fff', '&:hover': { bgcolor: '#14b8a6' } }}>
              <NavigateBeforeIcon />
            </IconButton>
          )}
          {premises.length > 1 && (
            <IconButton onClick={handleNextImage} sx={{ position: 'absolute', right: { xs: 8, md: 16 }, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(20,184,166,0.8)', color: '#fff', '&:hover': { bgcolor: '#14b8a6' } }}>
              <NavigateNextIcon />
            </IconButton>
          )}
          <Box component="img" src={premises[currentIndex]?.imageUrl} alt={premises[currentIndex]?.name}
            sx={{ maxWidth: { xs: '95vw', sm: '90vw' }, maxHeight: '85dvh', objectFit: 'contain', borderRadius: '12px', border: '3px solid rgba(20,184,166,0.5)' }}
          />
          <Box sx={{
            position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
          }}>
            <Box sx={{
              background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
              color: '#fff', px: 2.5, py: 0.5, borderRadius: '10px',
              fontWeight: 700, fontSize: '0.78rem', textAlign: 'center',
            }}>
              {premises[currentIndex]?.name}
            </Box>
            {premises.length > 1 && (
              <Box sx={{
                background: `linear-gradient(135deg, #14b8a6, #0d9488)`,
                color: '#fff', px: 2, py: 0.4, borderRadius: '10px',
                fontWeight: 700, fontSize: '0.72rem',
              }}>
                {currentIndex + 1} / {premises.length}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
